import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loader2, Star, X } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { GenerationDetailDTO } from '@/lib/types'
import { type ImageItem } from '@/hooks/use-image-popup'

interface ImagePopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  images: ImageItem[]
  currentImageIndex: number
  onImageIndexChange: (index: number) => void
  generationDetails?: Record<string, GenerationDetailDTO>
  onRegenerate?: (generationId: string) => Promise<void>
  onFavorite?: (assetId: string) => Promise<void>
  onDelete?: (generationId: string) => Promise<void>
  queryClient?: any
}

export function ImagePopup({
  open,
  onOpenChange,
  images,
  currentImageIndex,
  onImageIndexChange,
  generationDetails = {},
  onRegenerate,
  onFavorite,
  onDelete,
  queryClient,
}: ImagePopupProps) {
  const navigate = useNavigate()
  const [imageLoading, setImageLoading] = useState(true)
  const currentImage = images[currentImageIndex]
  const currentGenerationId = currentImage?.generationId

  // Fallback query for individual generation details when not in preloaded data
  const { data: fallbackDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['generation', 'detail', currentGenerationId],
    queryFn: () => api.generation.detail(currentGenerationId!),
    enabled: open && !!currentGenerationId && !generationDetails[currentGenerationId!],
  })

  const currentDetail = currentGenerationId
    ? generationDetails[currentGenerationId] || fallbackDetail
    : undefined

  // Reset loading state when image changes
  useEffect(() => {
    setImageLoading(true)
  }, [currentImageIndex])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, currentImageIndex, images.length])

  const handlePrevious = () => {
    if (currentImageIndex > 0) {
      onImageIndexChange(currentImageIndex - 1)
    } else {
      onImageIndexChange(images.length - 1) // Wrap around
    }
  }

  const handleNext = () => {
    if (currentImageIndex < images.length - 1) {
      onImageIndexChange(currentImageIndex + 1)
    } else {
      onImageIndexChange(0) // Wrap around
    }
  }

  const handleRegenerate = () => {
    if (!currentDetail || !currentGenerationId) return

    // Format loras as "id:strength,id:strength"
    const lorasString = currentDetail.loras.length > 0
      ? currentDetail.loras.map(lora => `${lora.id}:${lora.strength}`).join(',')
      : undefined

    navigate({
      to: '/generation',
      search: {
        loraId: undefined,
        modelSlug: currentDetail.base_model,
        prompt: currentDetail.prompt,
        width: currentDetail.width,
        height: currentDetail.height,
        steps: currentDetail.steps,
        cfgScale: currentDetail.cfg_scale,
        clipSkip: currentDetail.clip_skip,
        seedMode: 'random',
        seed: currentDetail.seed ?? undefined,
        loras: lorasString,
        workflowVariant: currentDetail.workflow_variant,
      },
    })
  }

  const handleFavorite = async () => {
    if (!onFavorite || !currentImage.assetId || !currentDetail) return

    try {
      // Optimistically update the UI immediately
      const currentAsset = currentDetail.assets.find(
        asset => asset.asset_id === currentImage.assetId
      )

      if (currentAsset) {
        // Update the image-popup query data with the new favorite state
        queryClient?.setQueryData(['image-popup', 'generations', [currentGenerationId]], (oldData: any) => {
          if (!oldData) return oldData

          const updatedData = { ...oldData }
          if (updatedData[currentGenerationId]) {
            updatedData[currentGenerationId] = {
              ...updatedData[currentGenerationId],
              assets: updatedData[currentGenerationId].assets.map((asset: any) =>
                asset.asset_id === currentImage.assetId
                  ? { ...asset, favorited: !asset.favorited }
                  : asset
              )
            }
          }
          return updatedData
        })

        // Also update the generation detail query if it exists
        queryClient?.setQueryData(['generation', 'detail', currentGenerationId], (oldData: any) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            assets: oldData.assets.map((asset: any) =>
              asset.asset_id === currentImage.assetId
                ? { ...asset, favorited: !asset.favorited }
                : asset
            )
          }
        })
      }

      await onFavorite(currentImage.assetId)
      queryClient?.invalidateQueries({ queryKey: ['generation', 'onload'] })
      queryClient?.invalidateQueries({ queryKey: ['gallery'] })
      queryClient?.invalidateQueries({ queryKey: ['image-popup'] })
    } catch (error: any) {
      // If the API call fails, revert the optimistic updates
      queryClient?.setQueryData(['image-popup', 'generations', [currentGenerationId]], (oldData: any) => {
        if (!oldData) return oldData

        const updatedData = { ...oldData }
        if (updatedData[currentGenerationId]) {
          updatedData[currentGenerationId] = {
            ...updatedData[currentGenerationId],
            assets: updatedData[currentGenerationId].assets.map((asset: any) =>
              asset.asset_id === currentImage.assetId
                ? { ...asset, favorited: !asset.favorited }
                : asset
            )
          }
        }
        return updatedData
      })

      queryClient?.setQueryData(['generation', 'detail', currentGenerationId], (oldData: any) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          assets: oldData.assets.map((asset: any) =>
            asset.asset_id === currentImage.assetId
              ? { ...asset, favorited: !asset.favorited }
              : asset
          )
        }
      })

      toast.error(error?.message ?? 'Failed to update favorite')
    }
  }

  const handleUpscale = () => {
    if (!currentDetail || !currentGenerationId) return

    // Format loras as "id:strength,id:strength"
    const lorasString = currentDetail.loras.length > 0
      ? currentDetail.loras.map(lora => `${lora.id}:${lora.strength}`).join(',')
      : undefined

    navigate({
      to: '/upscale',
      search: {
        generationId: currentGenerationId,
        assetIndex: currentImage.index ?? 0,
        modelSlug: currentDetail.base_model,
        prompt: currentDetail.prompt,
        steps: currentDetail.steps,
        cfgScale: currentDetail.cfg_scale,
        clipSkip: currentDetail.clip_skip,
        seedMode: currentDetail.seed_mode as 'fixed' | 'random' | undefined,
        seed: currentDetail.seed ?? undefined,
        loras: lorasString,
        workflowVariant: currentDetail.workflow_variant,
      },
    })
  }

  const handleDelete = async () => {
    if (!onDelete || !currentGenerationId) return
    if (!confirm('Are you sure you want to delete this generation? This action cannot be undone.')) return

    try {
      await onDelete(currentGenerationId)
      onOpenChange(false) // Close the popup after deletion
      toast.success('Generation deleted successfully')
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to delete generation')
    }
  }

  const handleDownload = async () => {
    try {
      if (!currentDetail) {
        toast.error('Generation details not loaded')
        return
      }

      const currentIndex = currentImage?.index ?? 0

      // For images, find the original PNG asset with the same index
      let downloadUrl: string | undefined
      let extension = 'png' // default

      if (isVideo) {
        // For videos, use the current asset's download URL
        downloadUrl = currentImage.downloadUrl ?? currentImage.src ?? undefined
        extension = 'mp4'
      } else {
        // For images, find the original PNG asset with the same index
        const originalAsset = currentDetail.assets.find(
          asset => asset.asset_type === 'image_original_png' && asset.index === currentIndex
        )

        if (originalAsset?.download_url) {
          downloadUrl = originalAsset.download_url
          extension = 'png'
        } else {
          // Fallback to current image's download URL
          downloadUrl = currentImage.downloadUrl ?? currentImage.src ?? undefined

          // Extract extension from asset type
          if (currentImage.assetType) {
            const assetTypeLower = currentImage.assetType.toLowerCase()
            if (assetTypeLower.includes('png')) {
              extension = 'png'
            } else if (assetTypeLower.includes('webp')) {
              extension = 'webp'
            } else if (assetTypeLower.includes('jpg') || assetTypeLower.includes('jpeg')) {
              extension = 'jpg'
            }
          }
        }
      }

      if (!downloadUrl) {
        toast.error('No download URL available')
        return
      }

      // Generate filename
      const filename = `image-${currentGenerationId}-${currentIndex}.${extension}`

      // For API download endpoints, let the browser handle the download naturally
      if (downloadUrl.startsWith('/api/generation/') && downloadUrl.includes('/download/')) {
        // Create a hidden link and trigger download
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // For direct URLs (R2, etc.), use the backend proxy to avoid CORS issues
        const proxyUrl = `/api/generation/download-proxy?url=${encodeURIComponent(downloadUrl)}`

        const link = document.createElement('a')
        link.href = proxyUrl
        link.download = filename
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download ' + (isVideo ? 'video' : 'image'))
    }
  }

  if (!currentImage) {
    return null
  }

  const isVideo = currentImage.isVideo || currentImage.assetType.startsWith('video')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="w-screen h-screen sm:w-[95vw] sm:h-[90vh] sm:max-h-[90vh] sm:max-w-none p-0 overflow-hidden sm:rounded-lg">
        <DialogHeader className="px-4 pt-4 pb-2 border-b sm:px-6 sm:pt-6 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-lg">Image details</DialogTitle>
          <div className="flex items-center gap-2">
            {onFavorite && currentImage.assetId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFavorite}
                disabled={isLoadingDetail}
                className="p-2 hover:bg-muted/80"
              >
                <Star
                  className={`h-5 w-5 ${
                    (() => {
                      // Check if the current asset is favorited by looking at the asset in the generation details
                      if (currentDetail?.assets) {
                        const currentAsset = currentDetail.assets.find(
                          asset => asset.asset_id === currentImage.assetId
                        )
                        return currentAsset?.favorited || false
                      }
                      return false
                    })()
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground'
                  }`}
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="p-2 hover:bg-muted/80"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-col h-[calc(100vh-5rem)] sm:h-[calc(90vh-8rem)]">
          <div className="flex flex-col sm:flex-row gap-4 h-full overflow-hidden">
            {/* Media Display - Takes up most of the space */}
            <div className="flex-1 flex flex-col space-y-3 min-h-0 order-1 sm:order-1">
              <div className="relative flex-1 flex items-center justify-center bg-black/5 rounded-lg overflow-hidden">
                {/* Media */}
                {isVideo ? (
                  <video
                    controls
                    className="w-full h-full object-contain"
                    src={currentImage.downloadUrl ?? ''}
                  />
                ) : (
                  <div className="relative w-full h-full">
                    {/* Thumbnail while loading */}
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted/20">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    )}

                    {/* Full resolution image */}
                    <img
                      src={currentImage.src ?? currentImage.downloadUrl ?? ''}
                      alt="Generated"
                      className={`w-full h-full object-contain transition-opacity duration-300 ${
                        imageLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoad={() => setImageLoading(false)}
                      onError={() => setImageLoading(false)}
                    />

                    {/* Loading indicator */}
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevious}
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 sm:p-3 transition-colors shadow-lg"
                      aria-label="Previous image"
                    >
                      <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 sm:p-3 transition-colors shadow-lg"
                      aria-label="Next image"
                    >
                      <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {images.length > 1 && (
                  <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-black/60 text-white text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>
            </div>

            {/* Details Panel - Fixed width sidebar */}
            <div className="w-full sm:w-80 space-y-4 text-sm overflow-y-auto pr-2 order-2 sm:order-2 max-h-[30vh] sm:max-h-none flex-shrink-0">
              {currentDetail ? (
                <>
                  <div className="border-b pb-4">
                    <h4 className="font-semibold text-base mb-2">Prompt</h4>
                    <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed text-sm">
                      {currentDetail.prompt}
                    </p>
                  </div>

                  <div className="border-b pb-4">
                    <h4 className="font-semibold mb-2">Settings</h4>
                    <ul className="space-y-1 text-muted-foreground">
                      <li className="flex justify-between">
                        <span>Model:</span>
                        <span className="font-medium">{currentDetail.base_model}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Steps:</span>
                        <span className="font-medium">{currentDetail.steps}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>CFG:</span>
                        <span className="font-medium">{currentDetail.cfg_scale}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Clip skip:</span>
                        <span className="font-medium">{currentDetail.clip_skip}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Size:</span>
                        <span className="font-medium">{currentDetail.width}×{currentDetail.height}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Seed:</span>
                        <span className="font-medium">{currentDetail.seed ?? '—'}</span>
                      </li>
                    </ul>
                  </div>

                  {currentDetail.loras.length > 0 && (
                    <div className="border-b pb-4">
                      <h4 className="font-semibold mb-2">LoRAs</h4>
                      <ul className="space-y-2 text-muted-foreground">
                        {currentDetail.loras.map((lora) => (
                          <li key={lora.id} className="flex justify-between items-center">
                            <span>{lora.display_name}</span>
                            <span className="font-medium text-xs bg-muted px-2 py-1 rounded">
                              {lora.strength}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : isLoadingDetail ? (
                <div className="text-muted-foreground text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  <p>Loading details...</p>
                </div>
              ) : (
                <div className="text-muted-foreground text-center py-8">
                  <p>No details available</p>
                </div>
              )}

              <div className="space-y-2">
                {onRegenerate && currentDetail?.job_type === 'generation' && (
                  <Button className="w-full" size="sm" onClick={handleRegenerate} disabled={isLoadingDetail}>
                    Regenerate
                  </Button>
                )}
                  <Button
                  className="w-full"
                  size="sm"
                  variant="outline"
                  onClick={handleUpscale}
                  disabled={!currentDetail || isLoadingDetail}
                >
                  Upscale
                </Button>
                <Button
                  className="w-full"
                  size="sm"
                  variant="secondary"
                  onClick={handleDownload}
                >
                  Download {isVideo ? 'Video' : 'Image'}
                </Button>
                {onDelete && (
                  <Button
                    className="w-full"
                    size="sm"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={isLoadingDetail}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}